import { PrivateURLConstants } from '@constants/private-url.constant';
import {
  getNextPageParam,
  useReactQueryCaching,
} from '@hooks/use-react-query-caching';
import {
  PROJECTS_INFINITE_QUERY_KEY,
  PROJECTS_QUERY_KEY,
} from '@modules/project-switcher/constants';
 
import { useInfiniteQuery, useQuery, useQueryClient } from 'react-query';
import api from '../api/instance';
import { Page } from '../models/page';
import { Project } from '../models/project.model';

const nameSpace = PrivateURLConstants.PROJECTS;

const findAll = (
  filterQuery?: string,
  page?: number,
  sort?: string,
  size?: number,
) => {
  return api
    .get(nameSpace, {
      query: filterQuery || '',
      page: page || 0,
      sort: sort || '',
      size: size || 0,
    })
    .then((res) => new Page<Project>().deserialize(res, Project))
    .catch((e) => {
      throw e;
    });
};

const show = (projectId: number) => {
  return api
    .get(nameSpace + '/' + projectId)
    .then((res) => new Project().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const create = (project: Project) => {
  return api
    .post(nameSpace, project.serialize())
    .then((res) => new Project().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const getAllProjects = (params: Record<string, any>) => {
  return api
    .get(nameSpace, params)
    .then((res) => new Page<Project>().deserialize(res, Project))
    .catch((e) => {
      throw e;
    });
};

const ProjectService = {
  findAll,
  show,
  create,
  getAllProjects,
};

export const useRefetchAllProjectsCache = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.refetchQueries(PROJECTS_INFINITE_QUERY_KEY);
    queryClient.refetchQueries(PROJECTS_QUERY_KEY);
  };
};

export const useGetAllProjects = ({
  filterQuery = '',
  keepPreviousData = false,
}) => {
  const { getCachingTime } = useReactQueryCaching(
    PROJECTS_INFINITE_QUERY_KEY,
    false,
  );

  return useInfiniteQuery({
    queryKey: [PROJECTS_INFINITE_QUERY_KEY, filterQuery],
    queryFn: ({ pageParam = 0 }) =>
      findAll(filterQuery, pageParam),
    getNextPageParam,
    staleTime: getCachingTime(),
    cacheTime: getCachingTime(),
    keepPreviousData,
  });
};

export const useQueryShowProject = ({
  projectId,
  enabled = true,
  onSuccess = (res: Project) => {},
}) => {
  const { getCachingTime } = useReactQueryCaching(PROJECTS_QUERY_KEY, false);

  return useQuery({
    queryKey: [PROJECTS_QUERY_KEY, projectId],
    queryFn: () => show(projectId),
    placeholderData: new Project(),
    enabled,
    staleTime: getCachingTime(),
    cacheTime: getCachingTime(),
    onSuccess,
  });
};

export default ProjectService;
